'use client';

import { useAppTheme } from '@/app/providers/ThemeProvider';
import { LG } from './liquidGlass';

/**
 * mode-aware Liquid Glass 토큰 훅.
 *
 * 모드(light/dark)를 자동으로 감지해 flat하게 해석된 토큰 객체를 반환한다.
 * 반환값은 바로 MUI `sx`에 spread하거나 인라인 style로 사용 가능하다.
 *
 * @example
 * ```tsx
 * const { panel, card, surfaceLayer1Flat, modal, element, type, divider, motion } = useLGTokens();
 *
 * <Box sx={{ ...panel, p: 2 }} />
 * <Typography sx={{ ...type.eyebrow }}>섹션 이름</Typography>
 * ```
 */
export function useLGTokens() {
  const { mode } = useAppTheme();
  const isDark = mode === 'dark';

  return {
    /** Layer 1 — 사이드바 / 탭바 크롬 */
    sidebar: isDark ? LG.sidebar.dark : LG.sidebar.light,

    /** Layer 2 — 플로팅 패널 (지도·배경 위) */
    panel: isDark ? LG.panel.dark : LG.panel.light,

    /** Layer 2 — 모달 오버레이 (panel보다 불투명) */
    modal: isDark ? LG.modal.dark : LG.modal.light,

    /** Layer 3 — 패널 내부 서브카드 */
    card: isDark ? LG.card.dark : LG.card.light,

    /** Layer 1 — 플랫 그룹 표면 (블러 없음, 짐 폼 서브블록 등) */
    surfaceLayer1Flat: isDark ? LG.surfaceLayer1Flat.dark : LG.surfaceLayer1Flat.light,

    /** Layer 1 수평 변형 — AppBar / Toolbar */
    appBar: isDark ? LG.appBar.dark : LG.appBar.light,

    /** Element Layer — 입력 필드 (blur 없음, focus ring glow) */
    input: isDark ? LG.input.dark : LG.input.light,

    /** Element Layer — 검색 pill glass (blur 8px) */
    searchBar: isDark ? LG.searchBar.dark : LG.searchBar.light,

    /** Layer 4 — 인라인 요소 */
    element: {
      pill: {
        /** 비활성 Pill */
        inactive: isDark ? LG.element.pill.inactive.dark : LG.element.pill.inactive.light,
        /** 활성 Pill — 브랜드 컬러, light/dark 동일 */
        active: LG.element.pill.active,
      },
      iconButton: {
        /** 기본 아이콘 버튼 */
        base: isDark ? LG.element.iconButton.dark : LG.element.iconButton.default,
        /** hover 상태 */
        hover: isDark ? LG.element.iconButton.darkHover : LG.element.iconButton.hover,
      },
    },

    /**
     * 타이포그래피 — 색상이 mode-resolved된 완전한 스타일 객체.
     * `<Typography sx={{ ...type.eyebrow }} />` 형태로 바로 사용.
     */
    type: {
      eyebrow: isDark ? LG.type.eyebrow.dark : LG.type.eyebrow.light,
      label: isDark ? LG.type.label.dark : LG.type.label.light,
      value: isDark ? LG.type.value.dark : LG.type.value.light,
      heading: isDark ? LG.type.heading.dark : LG.type.heading.light,
    },

    /** 구분선 */
    divider: isDark ? LG.divider.dark : LG.divider.light,

    /** 모션 토큰 (mode 무관) */
    motion: LG.motion,

    /** 현재 테마 모드 (조건부 로직에 사용) */
    isDark,
  } as const;
}

/** useLGTokens 반환 타입 */
export type LGTokens = ReturnType<typeof useLGTokens>;
